import type { Dapp } from '@src/dto/Dapp';
import {
  BarChart3,
  FileText,
  Headset,
  House,
  LayoutGrid,
  Smartphone,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import StarSeekerIcon from '../../assets/svg/star-seeker.svg?react';
import {
  getDappDetailsPath,
  getDappHomePath,
  getDappPathBySegment,
  getDappQuestsPath,
  getDappReleasesPath,
  getDappReviewsPath,
  getDappRouteSegment,
} from '../../utils/dappRoutes';
import { getDappMissingRequiredListingFields } from '../../utils/storeListingCompleteness';
import { StatusPill } from '../portal';
import { NavRow } from './NavRow';
import type { SidebarUtilityNavItem } from './utilityNav';

/** Exact match, or a descendant of `path` (dapp home never matches children). */
export function isActiveRoute(currentPath: string, path: string): boolean {
  if (currentPath === path) {
    return true;
  }
  return !/^\/dapp\/[^/]+$/.test(path) && currentPath.startsWith(`${path}/`);
}

interface DappNavProps {
  dapp: Dapp;
  currentPath: string;
  onLinkClick?: () => void;
}

/** NAVIGATION block: Overview / Releases / Ratings & Reviews / Store Listing / Quests. */
export function DappNav({ dapp, currentPath, onLinkClick }: DappNavProps) {
  // A saved draft counts toward completeness until it's published/discarded.
  const missingCount = getDappMissingRequiredListingFields({
    ...dapp,
    ...dapp.storeListingDraft?.values,
  }).length;
  const isUnreleasedDraft =
    dapp.status === 'Draft' &&
    (dapp.releaseCount === undefined || dapp.releaseCount === 0);

  const homePath = getDappHomePath(dapp);
  const releasesPath = getDappReleasesPath(dapp);
  const releaseDetailBase = getDappPathBySegment(
    getDappRouteSegment(dapp),
    '/release',
  );
  const reviewsPath = getDappReviewsPath(dapp);
  const detailsPath = getDappDetailsPath(dapp);
  const questsPath = getDappQuestsPath(dapp);

  const listingBadge: ReactNode =
    missingCount > 0 ? (
      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-900/40 px-1.5 text-xs font-bold leading-4 text-orange-500">
        <span aria-hidden="true">{missingCount}</span>
        <span className="sr-only">{missingCount} required fields missing</span>
      </span>
    ) : isUnreleasedDraft ? (
      <StatusPill tone="neutral">Draft</StatusPill>
    ) : null;

  return (
    <nav aria-label="App navigation" className="flex flex-col gap-1">
      <NavRow
        to={homePath}
        icon={<House />}
        label="Overview"
        active={currentPath === homePath}
        onClick={onLinkClick}
      />
      <NavRow
        to={releasesPath}
        icon={<Smartphone />}
        label="Releases"
        active={
          isActiveRoute(currentPath, releasesPath) ||
          isActiveRoute(currentPath, releaseDetailBase)
        }
        onClick={onLinkClick}
      />
      <NavRow
        to={reviewsPath}
        icon={<StarSeekerIcon className="fill-current" />}
        label="Ratings & Reviews"
        active={isActiveRoute(currentPath, reviewsPath)}
        onClick={onLinkClick}
      />
      <NavRow
        to={detailsPath}
        icon={<FileText />}
        label="Store Listing"
        active={isActiveRoute(currentPath, detailsPath)}
        trailing={listingBadge}
        onClick={onLinkClick}
      />
      {dapp.hasQuests && (
        <NavRow
          to={questsPath}
          icon={<Trophy />}
          label="Quests"
          active={isActiveRoute(currentPath, questsPath)}
          onClick={onLinkClick}
        />
      )}
    </nav>
  );
}

const ADMIN_ICONS: Record<SidebarUtilityNavItem['id'], ReactNode> = {
  dapps: <LayoutGrid />,
  publishers: <Users />,
  reporting: <BarChart3 />,
  tickets: <Headset />,
  aiSummaries: <Sparkles />,
};

interface AdminNavProps {
  items: SidebarUtilityNavItem[];
  currentPath: string;
  onLinkClick?: () => void;
}

/** ADMIN block for staff (Dapps / Publishers / Reporting / Tickets / AI Summaries). */
export function AdminNav({ items, currentPath, onLinkClick }: AdminNavProps) {
  return (
    <nav aria-label="Admin navigation" className="flex flex-col gap-1">
      {items.map((item) => (
        <NavRow
          key={item.id}
          to={item.to}
          icon={ADMIN_ICONS[item.id]}
          label={item.label}
          active={isActiveRoute(currentPath, item.to)}
          onClick={onLinkClick}
        />
      ))}
    </nav>
  );
}
